import type { ResolvedComponent } from "@inertiajs/react"
import { createInertiaApp } from "@inertiajs/react"
import posthog from "posthog-js"
import { PostHogProvider } from "posthog-js/react"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"

import PersistentLayout from "@/layouts/persistent-layout"
import { tokens } from "@/lib/tokens.generated"

const appName = import.meta.env.VITE_APP_NAME ?? "Blueprints"

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: (name) => {
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      "../pages/**/*.tsx",
      {
        eager: true,
      },
    )
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    page.default.layout ??= [PersistentLayout]

    return page
  },

  setup({ el, App, props }) {
    // Key and host come from the `analytics` shared prop, keyed from the
    // environment's credentials (config/initializers/analytics.rb) — never an
    // env var. It is null whenever analytics is off, and then nothing inits.
    const analytics = props.initialPage.props.analytics
    if (analytics && !import.meta.env.SSR) {
      posthog.init(analytics.key, {
        api_host: analytics.host,
        disable_session_recording: true, // started by PostHogIdentityProvider once signed in
        session_recording: { maskAllInputs: true },
        // Adds X-POSTHOG-SESSION-ID / X-POSTHOG-DISTINCT-ID headers to
        // fetch/XHR calls to our own host: Inertia visits are XHR, so every
        // navigation carries them.
        tracing_headers: [window.location.hostname],
        person_profiles: "identified_only",
      })
    }

    // Uncomment the following to enable SSR hydration:
    // if (el.hasChildNodes()) {
    //   hydrateRoot(el, <App {...props} />)
    //   return
    // }
    createRoot(el).render(
      <StrictMode>
        <PostHogProvider client={posthog}>
          <App {...props} />
        </PostHogProvider>
      </StrictMode>,
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },

  progress: {
    color: tokens.color.print[700],
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})
